import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ScenarioConfigurationService } from '../../service/scenario-configuration.service';
import { catchError, map, mergeMap, of } from 'rxjs';
import { ScenarioConfigurationActions } from './scenario-configuration.actions';

@Injectable()
export class ScenarioConfigurationEffects {

  loadScenarios$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(

        ofType(ScenarioConfigurationActions.loadScenarios),
        mergeMap(({ parameters, fields, scenarioType }) =>
          rs.getScenarios(scenarioType, parameters).pipe(
            map((scenarios) => {
              return ScenarioConfigurationActions.loadScenariosSuccess({
                scenarios,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadScenariosFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  loadScenariosHistory$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(

        ofType(ScenarioConfigurationActions.loadScenariosHistory),
        mergeMap(({ parameters, fields, scenarioType }) =>
          rs.getScenarioHistory(scenarioType, parameters).pipe(
            map((scenarios) => {
              return ScenarioConfigurationActions.loadScenariosHistorySuccess({
                scenarios,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadScenariosHistoryFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadSnapshots$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.loadSnapshots),
        mergeMap(({ parameters, fields }) =>
          rs.getSnapshots().pipe(
            map((snapshots) => {
              return ScenarioConfigurationActions.loadSnapshotsSuccess({
                snapshots,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadSnapshotsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );  

  createScenario$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.createScenario),
        mergeMap(({ scenario, parameters, scenarioType, fields }) =>
          rs.createScenario(scenario).pipe(
            map(() => {
              return ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields })
            }),
            catchError((error) => of(ScenarioConfigurationActions.createScenarioFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateScenarioNameOrVersion$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.updateScenarioNameOrVersion),
        mergeMap(({ name, parameters, scenarioIds, scenarioType, snapshotId, fields }) =>
          rs.updateScenarioNameOrVersion(name, scenarioIds, snapshotId).pipe(
            map(() => {
              return ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields })
            }),
            catchError((error) => of(ScenarioConfigurationActions.updateScenarioNameOrVersionFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  updateScenario$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.updateScenario),
        mergeMap(({ update, parameters, scenarioType, fields }) =>
          rs.updateScenario(update).pipe(
            map(() => {
              return ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields })
            }),
            catchError((error) => of(ScenarioConfigurationActions.updateScenarioFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadLegalEntities$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.loadLegalEntities),
        mergeMap(({ parameters, fields, snapshotId }) =>
          rs.getLegalEntitiesBySnapshot(snapshotId, parameters).pipe(
            map((legalEntities) => {
              return ScenarioConfigurationActions.loadLegalEntitiesSuccess({
                legalEntities,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadLegalEntitiesFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllLegalEntities$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.loadAllLegalEntities),
        mergeMap(({ parameters, fields, snapshotId }) =>
          rs.getLegalEntitiesBySnapshot(snapshotId, parameters).pipe(
            map((legalEntities) => {
              return ScenarioConfigurationActions.loadAllLegalEntitiesSuccess({
                legalEntities,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadAllLegalEntitiesFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadCpts$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.loadCpts),
        mergeMap(({ parameters, fields, snapshotId, legalEntityTrackingIds }) =>
          rs.getCptBySnapshot(snapshotId, parameters, legalEntityTrackingIds).pipe(
            map((cpts) => {
              return ScenarioConfigurationActions.loadCptsSuccess({
                cpts,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadCptsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllCpts$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.loadAllCpts),
        mergeMap(({ parameters, fields, snapshotId, legalEntityTrackingIds }) =>
          rs.getCptBySnapshot(snapshotId, parameters, legalEntityTrackingIds).pipe(
            map((cpts) => {
              return ScenarioConfigurationActions.loadAllCptsSuccess({
                cpts,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadAllCptsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadRisks$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.loadRisks),
        mergeMap(({ parameters, fields }) =>
          rs.getRisks(parameters).pipe(
            map((risks) => {
              return ScenarioConfigurationActions.loadRisksSuccess({
                risks,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadRisksFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllRisks$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.loadAllRisks),
        mergeMap(({ parameters, fields }) =>
          rs.getRisks(parameters).pipe(
            map((risks) => {
              return ScenarioConfigurationActions.loadAllRisksSuccess({
                risks,
                fields
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.loadAllRisksFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  updateScenarioLegalEntity$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.updateScenarioLegalEntity),
        mergeMap(({ ids, legalEntityIds, parameters, scenarioType, fields }) =>
          rs.updateScenarioLegalEntities(ids, legalEntityIds).pipe(
            map(() => {
              return ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields })
            }),
            catchError((error) => of(ScenarioConfigurationActions.updateScenarioLegalEntityFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  updateScenarioProcess$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.updateScenarioProcess),
        mergeMap(({ ids, processIds, parameters, scenarioType, fields }) =>
          rs.updateScenarioProcess(ids, processIds).pipe(
            map(() => {
              return ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields })
            }),
            catchError((error) => of(ScenarioConfigurationActions.updateScenarioProcessFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateScenarioRisk$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.updateScenarioRisk),
        mergeMap(({ ids, riskIds, parameters, scenarioType, fields }) =>
          rs.updateScenarioRisk(ids, riskIds).pipe(
            map(() => {
              return ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields })
            }),
            catchError((error) => of(ScenarioConfigurationActions.updateScenarioRiskFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteScenarios$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.deleteScenarios),
        mergeMap(({ ids, parameters, scenarioType, fields }) =>
          rs.deleteScenarios(ids).pipe(
            map(() => {
              return ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields })
            }),
            catchError((error) => of(ScenarioConfigurationActions.deleteScenariosFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  launchScenario$ = createEffect(
    (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.launchScenario),
        mergeMap(({ id, parameters, scenarioType, fields, historyParameters, historyFields }) =>
          rs.launchScenario(id).pipe(
            mergeMap(() => [
              ScenarioConfigurationActions.loadScenarios({ parameters, scenarioType, fields }),
              ScenarioConfigurationActions.loadScenariosHistory({ parameters: historyParameters, scenarioType, fields: historyFields })
            ]),
            catchError((error) => of(ScenarioConfigurationActions.launchScenarioFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  // downloadScenarioHeatMap$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(ScenarioConfigurationActions.downloadScenario),
  //     mergeMap(({ scenarioId, valueChain, timeZone }) =>
  //       this.scenarioService.downloadScenarioHeatMap(scenarioId, valueChain, timeZone).pipe(
  //         map((response) => {
  //           // Qui estrai filename e contenuto dalla response
  //           const content = response.data?.content;
  //           const filename = response.data?.filename;
  //           return ScenarioConfigurationActions.downloadScenarioSuccess({ content, filename });
  //         }),
  //         catchError((error) =>
  //           of(ScenarioConfigurationActions.downloadScenarioFailure({ error }))
  //         )
  //       )
  //     )
  //   )
  // );

  
// exportScenario$ = createEffect(
//   (rs: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
//     return this.actions$.pipe(
//       ofType(ScenarioConfigurationActions.exportScenario),
//       mergeMap(({ scenarioId, valueChain,timeZone }) =>
//         rs.exportScenario(scenarioId, valueChain,timeZone).pipe(
//           map((data) => {
     
//             const blob = new Blob([atob(data?.content)], { type: 'application/octet-stream' });
//             const link = document.createElement('a');
//             link.href = window.URL.createObjectURL(blob);
//             link.download = data.filename || 'scenario-export.xlsx';
//             link.click();
//             return ScenarioConfigurationActions.exportScenarioSuccess();
//           }),
//           catchError((error) =>
//             of(ScenarioConfigurationActions.exportScenarioFailure({ error }))
//           )
//         )
//       )
//     );
//   },
//   { functional: true }
// );


  exportScenario$ = createEffect(
    (as: ScenarioConfigurationService = inject(ScenarioConfigurationService)) => {
      return this.actions$.pipe(
        ofType(ScenarioConfigurationActions.exportScenario),
        mergeMap(({  scenarioId, valueChain,timeZone  }) =>
          as.downaloadHeatmaps( scenarioId, valueChain, timeZone
          ).pipe(
            map((file) => {
              this.downloadFile(
                file!.data![0]!.content,
                file!.data![0]!.filename
              );
              return ScenarioConfigurationActions.exportScenarioSuccess({
                heatmap: file
              });
            }),
            catchError((error) => of(ScenarioConfigurationActions.exportScenarioFailure({ error })))
          )
        )
      )
    }
  )

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }


  constructor(private actions$: Actions) { }
}
